import React, { useEffect, useState } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Paper,
  Box,
  CircularProgress,
  Backdrop,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Dialog,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from "@mui/material";
import { Add, Assignment, Business, ChecklistRtl, Delete, DragIndicator, Edit, ExpandMore, Gavel, UploadFile } from "@mui/icons-material";
import { ChromePicker } from 'react-color';
import { useOffice } from "../../store/officeContext";
import { supabase } from "../../supabaseClient";
import { useParams } from "react-router-dom";

const colorOptions = [
  { label: 'Azul oscuro', value: '#003366' },
  { label: 'Azul claro', value: '#3498db' },
  { label: 'Verde esmeralda', value: '#2ecc71' },
  { label: 'Verde oliva', value: '#808000' },
  { label: 'Gris neutro', value: '#7f8c8d' },
  { label: 'Negro elegante', value: '#000000' },
  { label: 'Rojo burdeos', value: '#8e0e00' },
  { label: 'Morado corporativo', value: '#6a0dad' },
  { label: 'Naranja suave', value: '#f39c12' },
  { label: 'Personalizar...', value: 'custom' }
];

// ==================== MODAL DE TAREAS PLANTILLA (FUERA DEL COMPONENTE PRINCIPAL) ====================
const TaskTemplateModal = React.memo(({ 
  open, 
  onClose, 
  selectedStatus, 
  taskTemplates,
  newTaskName,
  setNewTaskName,
  editingTask,
  setEditingTask,
  addTaskTemplate,
  updateTaskTemplate,
  deleteTaskTemplate,
  moveTaskUp,
  moveTaskDown,
  officeColor
}) => {
  const office = useOffice().office;
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChecklistRtl color="primary" />
          <Typography variant="h6">
            Tareas Plantilla: {selectedStatus?.name}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Estas tareas se crearán automáticamente para cada expediente con este subtipo
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Formulario para agregar nueva tarea */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Nueva tarea"
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && newTaskName.trim()) {
                addTaskTemplate();
              }
            }}
            placeholder="Ej: Recopilar documentación del cliente"
          />
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={addTaskTemplate}
            disabled={!newTaskName.trim()}
            sx={{
              backgroundColor: officeColor,
              color: "#fff",
              '&:hover': {
                backgroundColor: officeColor,
              },
            }}
          >
            Agregar
          </Button>
        </Box>

        {/* Lista de tareas */}
        {taskTemplates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No hay tareas plantilla creadas para este subtipo.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Agrega tareas que se crearán automáticamente cuando un expediente tenga este subtipo.
            </Typography>
          </Box>
        ) : (
          <List>
            {taskTemplates.map((task, index) => (
              <ListItem
                key={task.id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: '#fafafa'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => moveTaskUp(task, index)}
                    disabled={index === 0}
                  >
                    <ExpandMore sx={{ transform: 'rotate(180deg)', color: office?.color }} fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => moveTaskDown(task, index)}
                    disabled={index === taskTemplates.length - 1}
                  >
                    <ExpandMore fontSize="small" />
                  </IconButton>
                  <DragIndicator fontSize="small" color="action" />
                </Box>

                {editingTask?.id === task.id ? (
                  <TextField
                    fullWidth
                    value={editingTask.name}
                    onChange={(e) => setEditingTask({ ...editingTask, name: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        updateTaskTemplate(editingTask);
                      }
                    }}
                    onBlur={() => updateTaskTemplate(editingTask)}
                    autoFocus
                    size="small"
                  />
                ) : (
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {index + 1}. {task.name}
                      </Typography>
                    }
                  />
                )}

                <ListItemSecondaryAction>
                  <IconButton 
                    size="small" 
                    onClick={() => setEditingTask(task)}
                    disabled={editingTask?.id === task.id}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={() => deleteTaskTemplate(task.id)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, ml: 2 }}>
          {taskTemplates.length} tarea{taskTemplates.length !== 1 ? 's' : ''} configurada{taskTemplates.length !== 1 ? 's' : ''}
        </Typography>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
});

// Agregar displayName para debugging
TaskTemplateModal.displayName = 'TaskTemplateModal';

const OfficeSettingsView = () => {
  const { officeId } = useParams()
  const office = useOffice().office;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados para puestos de trabajo
  const [jobPositions, setJobPositions] = useState([]);
  const [newPositionName, setNewPositionName] = useState("");

  // Estados para áreas de práctica
  const [practiceAreas, setPracticeAreas] = useState([]); // Todas las áreas disponibles
  // const [officePracticeAreas, setOfficePracticeAreas] = useState([]); // Áreas elegidas por la oficina
  // const [selectedPracticeAreaId, setSelectedPracticeAreaId] = useState('');

  const [hierarchies, setHierarchies] = useState([])
  const [officeHierarchies, setOfficeHierarchies] = useState([]);

  const [selectedHierarchyId, setSelectedHierarchyId] = useState('');
  const [expandedHierarchy, setExpandedHierarchy] = useState(null);

  // Estados para tipos de expediente
  const [caseTypes, setCaseTypes] = useState([]); // Tipos de expediente

  // Estados para edición de datos del despacho
  const [officeForm, setOfficeForm] = useState({
    name: '',
    document_number: '',
    contact_number: '',
    email: '',
    color: '',
    icon: null
  });
  const [iconPreview, setIconPreview] = useState(null);
  const [customColor, setCustomColor] = useState('#6A0DAD');
  const [officeUpdateLoading, setOfficeUpdateLoading] = useState(false);
  const [officeUpdateError, setOfficeUpdateError] = useState(null);
  const [officeUpdateSuccess, setOfficeUpdateSuccess] = useState(false);

  // Estados para forms (formas)
  const [officeForms, setOfficeForms] = useState([]);

  // Estados para tipos de expediente (renombrar los actuales)
  // Mantener: caseTypes, pero cambiar la lógica

  // Estados para status (NUEVO)
  const [officeStatuses, setOfficeStatuses] = useState([]);

  // Estados para diálogos (NUEVO - reemplaza múltiples diálogos)
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({ 
    type: '', // 'form', 'type', 'status'
    data: null, // Para edición
    context: null // Para creación
  });

  // Acordeones expandibles
  const [expandedArea, setExpandedArea] = useState(null);

  // Estados para tareas plantilla (NUEVO)
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedStatusForTasks, setSelectedStatusForTasks] = useState(null);
  const [newTaskName, setNewTaskName] = useState('');
  const [editingTask, setEditingTask] = useState(null);

  // Estados generales
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('office-data');

  const primaryColorStyles = {
    backgroundColor: office.color,
    color: "#fff",
    '&:hover': {
      backgroundColor: office.color,
    },
  };

/*   const primaryOutlineStyles = {
    borderColor: office.color,
    color: office.color,
    '&:hover': {
      backgroundColor: office.color,
      color: "#fff",
    },
  }; */

  useEffect(() => {
    if (office?.id) {
      loadJobPositions();
      loadPracticeAreas();
      initializeOfficeForm();
    }
  }, [office]);

  // useEffect(() => {
  //   if (officePracticeAreas.length > 0) {
  //     loadOfficeForms();
  //   }
  // }, [officePracticeAreas]);

  useEffect(() => {
    if (officeForms.length > 0) {
      loadCaseTypes();
      loadOfficeStatuses();
    }
  }, [officeForms]);

  useEffect(() => {
    fetchHierarchies()
    fetchOfficeHierarchies()
  }, [officeId])

  // ==================== FUNCIONES PARA DATOS DEL DESPACHO ====================
  const initializeOfficeForm = () => {
    setOfficeForm({
      name: office.name || '',
      document_number: office.document_number || '',
      contact_number: office.contact_number || '',
      email: office.email || '',
      color: office.color || '',
      icon: null
    });
    
    if (office.icon_url) {
      setIconPreview(getIconUrl(office.icon_url));
    }
    
    if (office.color && !colorOptions.find(opt => opt.value === office.color)) {
      setCustomColor(office.color);
    }
  };

  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;
    
    // Si ya es una URL completa, devolverla tal como está
    if (iconPath.startsWith('http')) return iconPath;
    
    // Si es un path relativo, construir la URL pública
    const { data } = supabase.storage
      .from('files')
      .getPublicUrl(iconPath);
    
    return data.publicUrl;
  };

  const handleOfficeFormChange = (field) => (event) => {
    const value = event.target.value;
    setOfficeForm({ ...officeForm, [field]: value });
    setOfficeUpdateError(null);
    setOfficeUpdateSuccess(false);

    if (field === 'color' && value === 'custom') {
      setOfficeForm({ ...officeForm, color: customColor });
    }
  };

  const handleOfficeFileChange = (event) => {
    const file = event.target.files[0];
    setOfficeUpdateError(null);
    setOfficeUpdateSuccess(false);
    
    if (file) {
      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setOfficeUpdateError('Solo se permiten archivos de imagen (JPG, PNG, GIF, WEBP)');
        return;
      }

      // Validar tamaño (máximo 2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB en bytes
      if (file.size > maxSize) {
        setOfficeUpdateError('El archivo debe ser menor a 2MB');
        return;
      }

      setOfficeForm({ ...officeForm, icon: file });
      
      // Crear preview
      const reader = new FileReader();
      reader.onload = () => setIconPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleOfficeCustomColorChange = (color) => {
    setCustomColor(color.hex);
    setOfficeForm((prev) => ({ ...prev, color: color.hex }));
    setOfficeUpdateError(null);
    setOfficeUpdateSuccess(false);
  };

  const uploadOfficeIcon = async (officeId, iconFile) => {
    try {
      // Generar nombre único para el archivo
      const fileExtension = iconFile.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExtension}`;
      
      // Definir la ruta del archivo
      const filePath = `office/${officeId}/icons/${fileName}`;
      
      // Subir archivo a Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('files')
        .upload(filePath, iconFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Error al subir archivo: ${uploadError.message}`);
      }

      return filePath;
    } catch (error) {
      console.error('Error en uploadOfficeIcon:', error);
      throw error;
    }
  };

  const handleUpdateOffice = async (e) => {
    e.preventDefault();
    setOfficeUpdateLoading(true);
    setOfficeUpdateError(null);
    setOfficeUpdateSuccess(false);

    try {
      // Preparar objeto de actualización (sin icon_url inicialmente)
      const updateObj = {
        name: officeForm.name,
        document_number: officeForm.document_number,
        contact_number: officeForm.contact_number,
        email: officeForm.email,
        color: officeForm.color
      };

      // Subir nuevo ícono si existe
      if (officeForm.icon) {
        try {
          const iconUrl = await uploadOfficeIcon(office.id, officeForm.icon);
          updateObj.icon_url = iconUrl;
        } catch (iconError) {
          console.error('Error al subir ícono:', iconError);
          throw new Error('Error al subir el nuevo ícono');
        }
      }

      // Actualizar office en base de datos
      const { data, error } = await supabase
        .from('office')
        .update(updateObj)
        .eq('id', office.id)
        .select()
        .single();

      if (error) {
        throw new Error(`Error al actualizar el despacho: ${error.message}`);
      }

      setOfficeUpdateSuccess(true);
      
      // Resetear el campo de archivo
      setOfficeForm(prev => ({ ...prev, icon: null }));

      // Actualizar el preview con la nueva imagen
      if (updateObj.icon_url) {
        setIconPreview(getIconUrl(updateObj.icon_url));
      }

      // Opcional: Actualizar el contexto si tienes una función para ello
      // refreshOfficeContext();

    } catch (error) {
      console.error('Error en handleUpdateOffice:', error);
      setOfficeUpdateError(error.message || 'Error al actualizar el despacho');
    } finally {
      setOfficeUpdateLoading(false);
    }
  };

  // ==================== FUNCIONES PARA PUESTOS DE TRABAJO ====================
  const loadJobPositions = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from("job_position")
      .select("*")
      .eq("office_id", office.id);

    if (!error) {
      setJobPositions(data || []);
    } else {
      console.error("Error loading job positions:", error.message);
    }

    setLoading(false);
  };

  const addJobPosition = async () => {
    if (!newPositionName.trim()) return;

    const { data, error } = await supabase
      .from("job_position")
      .insert([{ name: newPositionName, office_id: office.id }])
      .select();

    if (!error && data) {
      setJobPositions((prev) => [...prev, ...data]);
      setNewPositionName("");
    } else {
      console.error("Error adding job position:", error.message);
    }
  };

  const deleteJobPosition = async (id) => {
    const { error } = await supabase
      .from("job_position")
      .delete()
      .eq("id", id);

    if (!error) {
      setJobPositions((prev) => prev.filter((p) => p.id !== id));
    } else {
      console.error("Error deleting job position:", error.message);
    }
  };

  // ==================== FUNCIONES PARA ÁREAS DE PRÁCTICA ====================
  const loadPracticeAreas = async () => {
    setLoading(true);

    // Cargar todas las áreas disponibles
    const { data: allAreas, error: areasError } = await supabase
      .from("practice_area")
      .select("*")
      .order("name");

    // Cargar áreas de la oficina
    // const { data: officeAreas, error: officeAreasError } = await supabase
    //   .from("office_practice_area")
    //   .select(`
    //     id,
    //     practice_area_id,
    //     practice_area (
    //       id,
    //       name
    //     )
    //   `)
    //   .eq("office_id", office.id);

    if (!areasError) {
      setPracticeAreas(allAreas || []);
    } else {
      console.error("Error loading practice areas:", areasError.message);
    }

    // if (!officeAreasError) {
    //   setOfficePracticeAreas(officeAreas || []);
    // } else {
    //   console.error("Error loading office practice areas:", officeAreasError.message);
    // }

    setLoading(false);
  };

  // const loadOfficeForms = async () => {
  //   if (officePracticeAreas.length === 0) {
  //     setOfficeForms([]);
  //     return;
  //   }

  //   const officePracticeAreaIds = officePracticeAreas.map(opa => opa.id);

  //   const { data, error } = await supabase
  //     .from("hierarchy_form")
  //     .select("*")
  //     .in('office_practice_area_id', officePracticeAreaIds)
  //     .order('display_order');

  //   if (!error) {
  //     setOfficeForms(data || []);
  //   }
  // };

  const loadOfficeStatuses = async () => {
    if (officeForms.length === 0) {
      setOfficeStatuses([]);
      return;
    }

    // Primero cargar los estatus
    const { data: statusData, error: statusError } = await supabase
      .from("office_status")
      .select("*")
      .in('form_id', officeForms.map(f => f.id))
      .order('display_order');

    if (statusError) {
      console.error("Error loading statuses:", statusError);
      return;
    }

    // Luego cargar los conteos de tareas para cada estatus
    const statusIds = statusData.map(s => s.id);
    
    const { data: taskCounts, error: taskError } = await supabase
      .from("task_template")
      .select("office_status_id")
      .in('office_status_id', statusIds);

    if (taskError) {
      console.error("Error loading task counts:", taskError);
      setOfficeStatuses(statusData || []);
      return;
    }

    // Contar las tareas por estatus
    const countMap = taskCounts.reduce((acc, task) => {
      acc[task.office_status_id] = (acc[task.office_status_id] || 0) + 1;
      return acc;
    }, {});

    // Agregar el conteo a cada estatus
    const statusesWithCount = statusData.map(status => ({
      ...status,
      task_count: countMap[status.id] || 0
    }));

    setOfficeStatuses(statusesWithCount);
  };

  // const addPracticeAreaToOffice = async () => {
  //   if (!selectedPracticeAreaId) return;

  //   const { data, error } = await supabase
  //     .from("office_practice_area")
  //     .insert([{ 
  //       office_id: office.id, 
  //       practice_area_id: selectedPracticeAreaId 
  //     }])
  //     .select(`
  //       id,
  //       practice_area_id,
  //       practice_area (
  //         id,
  //         name
  //       )
  //     `);

  //   if (!error && data) {
  //     setOfficePracticeAreas(prev => [...prev, ...data]);
  //     setSelectedPracticeAreaId('');
  //   } else {
  //     console.error("Error adding practice area to office:", error.message);
  //   }
  // };

  // const removePracticeAreaFromOffice = async (id) => {
  //   const { error } = await supabase
  //     .from("office_practice_area")
  //     .delete()
  //     .eq("id", id);

  //   if (!error) {
  //     setOfficePracticeAreas(prev => prev.filter(area => area.id !== id));
  //   } else {
  //     console.error("Error removing practice area from office:", error.message);
  //   }
  // };

  const loadCaseTypes = async () => {
    if (officeForms.length === 0) {
      setCaseTypes([]);
      return;
    }

    const { data, error } = await supabase
      .from("case_type")
      .select("*")
      .in('hierarchy_form_id', officeForms.map(f => f.id));

    if (!error) {
      setCaseTypes(data || []);
    }
  };

  const addCaseType = async (hierarchyFormId, name) => {
    try {
      const { error } = await supabase
        .from('case_type')
        .insert({
          name,
          hierarchy_form_id: hierarchyFormId
        });

      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error creando tipo:', err);
    }
  };

  const updateCaseType = async (type) => {
    try {
      const { error } = await supabase
        .from('case_type')
        .update({
          name: type.name
        })
        .eq('id', type.id);

      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error actualizando tipo:', err);
    }
  };

  const deleteType = async (typeId) => {
    try {
      // 1. Borrar statuses del tipo
      const { error: statusError } = await supabase
        .from('office_status')
        .delete()
        .eq('type_id', typeId);
      if (statusError) throw statusError;
      // 2. Borrar tipo
      const { error } = await supabase
        .from('case_type')
        .delete()
        .eq('id', typeId);

      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error deleting type:', err);
    }
  };

  const addHierarchyForm = async (hierarchyId, name) => {
    try {
      // 1. obtener cantidad actual de forms
      const { data: existingForms, error: countError } = await supabase
        .from('hierarchy_form')
        .select('id')
        .eq('hierarchy_id', hierarchyId);

      if (countError) throw countError;
      const displayOrder = (existingForms?.length || 0) + 1;
      // 2. insertar
      const { error } = await supabase
        .from('hierarchy_form')
        .insert({
          name,
          hierarchy_id: hierarchyId,
          display_order: displayOrder
        });
      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error creando forma:', err);
    }
  };

  const updateHierarchyForm = async (form) => {
    try {
      const { error } = await supabase
        .from('hierarchy_form')
        .update({
          name: form.name
        })
        .eq('id', form.id);

      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error actualizando forma:', err);
    }
  };

  const deleteForm = async (formId) => {
    try {
      // 1. Traer tipos
      const { data: types } = await supabase
        .from('case_type')
        .select('id')
        .eq('hierarchy_form_id', formId);
      const typeIds = types?.map(t => t.id) || [];
      // 2. Borrar statuses
      await supabase
        .from('office_status')
        .delete()
        .or(
          `form_id.eq.${formId},type_id.in.(${typeIds.join(',')})`
        );
      // 3. Borrar tipos
      if (typeIds.length > 0) {
        await supabase
          .from('case_type')
          .delete()
          .in('id', typeIds);
      }
      // 4. Borrar form
      const { error } = await supabase
        .from('hierarchy_form')
        .delete()
        .eq('id', formId);
      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error deleting form:', err);
    }
  };

  const addOfficeStatus = async (formId, typeId, name) => {
    try {
      // 1. obtener orden actual
      let query = supabase
        .from('office_status')
        .select('id')
        .eq('form_id', formId);

      if (typeId) {
        query = query.eq('type_id', typeId);
      } else {
        query = query.is('type_id', null);
      }
      const { data: existing, error: countError } = await query;
      if (countError) throw countError;
      const displayOrder = (existing?.length || 0) + 1;
      // 2. insertar
      const { error } = await supabase
        .from('office_status')
        .insert({
          name,
          form_id: formId,
          type_id: typeId || null,
          display_order: displayOrder
        });
      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error creando estatus:', err);
    }
  };

  const updateOfficeStatus = async (status) => {
    try {
      const { error } = await supabase
        .from('office_status')
        .update({
          name: status.name
        })
        .eq('id', status.id);
      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error actualizando estatus:', err);
    }
  };

  const deleteOfficeStatus = async (id) => {
    try {
      // 1. Validar si hay cases usando este status
      const { count, error: caseError } = await supabase
        .from('case')
        .select('*', { count: 'exact', head: true })
        .eq('office_status_id', id);

      if (caseError) throw caseError;
      if (count > 0) {
        alert('No puedes eliminar este subtipo porque tiene expedientes asociados');
        return;
      }

      // 2. Eliminar
      const { error } = await supabase
        .from('office_status')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setOfficeStatuses(prev => prev.filter(s => s.id !== id));
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error eliminando status:', err);
      alert('Error eliminando subtipo');
    }
  };

  const updateHierarchy = async (hierarchy) => {
    try {
      const { error } = await supabase
        .from('hierarchy')
        .update({
          practice_area_id: hierarchy.practice_area_id
        })
        .eq('id', hierarchy.id);

      if (error) throw error;
      await fetchOfficeHierarchies();
    } catch (err) {
      console.error('Error actualizando jerarquía:', err);
    }
  };

  const deleteOfficeHierarchy = async (hierarchyId) => {
    try {
      setLoading(true);

      // 1. Obtener todos los office_status asociados a la jerarquía
      const { data: statusData, error: statusError } = await supabase
        .from('office_status')
        .select(`
          id,
          hierarchy_form!inner (
            hierarchy_id
          )
        `)
        .eq('hierarchy_form.hierarchy_id', hierarchyId);

      if (statusError) throw statusError;
      const statusIds = (statusData || []).map(s => s.id);
      // 2. Validar si hay cases asociados
      if (statusIds.length > 0) {
        const { count, error: caseError } = await supabase
          .from('case')
          .select('*', { count: 'exact', head: true })
          .in('office_status_id', statusIds);

        if (caseError) throw caseError;

        if (count > 0) {
          alert(
            'No puedes eliminar esta jerarquía porque tiene expedientes asociados'
          );
          return;
        }
      }

      // 3. Eliminar en orden correcto (por FK)
      // primero statuses
      if (statusIds.length > 0) {
        const { error: statusDeleteError } = await supabase
          .from('office_status')
          .delete()
          .in('id', statusIds);

        if (statusDeleteError) throw statusDeleteError;
      }

      // luego types
      const { data: formsData, error: formsError } = await supabase
        .from('hierarchy_form')
        .select('id')
        .eq('hierarchy_id', hierarchyId);
      if (formsError) throw formsError;
      const formIds = (formsData || []).map(f => f.id);

      if (formIds.length > 0) {
        const { error: typeDeleteError } = await supabase
          .from('case_type')
          .delete()
          .in('hierarchy_form_id', formIds);
        if (typeDeleteError) throw typeDeleteError;
      }
      // luego forms
      if (formIds.length > 0) {
        const { error: formDeleteError } = await supabase
          .from('hierarchy_form')
          .delete()
          .in('id', formIds);
        if (formDeleteError) throw formDeleteError;
      }
      const { error: officeHierarchyError } = await supabase
        .from('office_hierarchy')
        .delete()
        .eq('hierarchy_id', hierarchyId);
      if (officeHierarchyError) throw officeHierarchyError;

      // finalmente hierarchy
      const { error: hierarchyDeleteError } = await supabase
        .from('hierarchy')
        .delete()
        .eq('id', hierarchyId);

      if (hierarchyDeleteError) throw hierarchyDeleteError;
      alert('Jerarquía eliminada correctamente');
      fetchOfficeHierarchies();
    } catch (error) {
      console.error('Error deleting hierarchy:', error);
      alert('Error eliminando jerarquía');
    } finally {
      setLoading(false);
    }
  };

  const getFormsByOfficePracticeArea = (officePracticeAreaId) => {
    return officeForms.filter(f => f.office_practice_area_id === officePracticeAreaId);
  };

  const openDialog = (type, data = null, context = null) => {
    setDialogConfig({ type, data, context });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogConfig({ type: '', data: null, context: null });
  };

  // ==================== FUNCIONES PARA TAREAS PLANTILLA ====================
  const loadTaskTemplates = async (statusId) => {
    try {
      const { data, error } = await supabase
        .from("task_template")
        .select("*")
        .eq("office_status_id", statusId)
        .order("display_order");

      if (error) throw error;
      setTaskTemplates(data || []);
    } catch (error) {
      console.error("Error loading task templates:", error);
      setTaskTemplates([]);
    }
  };

  const openTaskModal = async (status) => {
    setSelectedStatusForTasks(status);
    setTaskModalOpen(true);
    await loadTaskTemplates(status.id);
  };

  const closeTaskModal = () => {
    setTaskModalOpen(false);
    setSelectedStatusForTasks(null);
    setTaskTemplates([]);
    setNewTaskName('');
    setEditingTask(null);
  };

  const updateTaskTemplate = async (task) => {
    const { error } = await supabase
      .from("task_template")
      .update({ name: task.name })
      .eq("id", task.id);

    if (!error) {
      setTaskTemplates(prev => prev.map(t => t.id === task.id ? task : t));
      setEditingTask(null);
    }
  };

  const addTaskTemplate = async () => {
    if (!newTaskName.trim() || !selectedStatusForTasks) return;

    const maxOrder = Math.max(0, ...taskTemplates.map(t => t.display_order || 0));

    const { data, error } = await supabase
      .from("task_template")
      .insert([{
        office_status_id: selectedStatusForTasks.id,
        name: newTaskName,
        display_order: maxOrder + 1
      }])
      .select();

    if (!error && data) {
      setTaskTemplates(prev => [...prev, ...data]);
      setNewTaskName('');
      
      // Actualizar el contador en el chip
      setOfficeStatuses(prev => 
        prev.map(s => 
          s.id === selectedStatusForTasks.id 
            ? { ...s, task_count: (s.task_count || 0) + 1 }
            : s
        )
      );
    }
  };

  const deleteTaskTemplate = async (id) => {
    const { error } = await supabase
      .from("task_template")
      .delete()
      .eq("id", id);

    if (!error) {
      setTaskTemplates(prev => prev.filter(t => t.id !== id));
      
      // Actualizar el contador en el chip
      setOfficeStatuses(prev => 
        prev.map(s => 
          s.id === selectedStatusForTasks.id 
            ? { ...s, task_count: Math.max(0, (s.task_count || 0) - 1) }
            : s
        )
      );
    }
  };

  const moveTaskUp = async (task, index) => {
    if (index === 0) return;
    const prevTask = taskTemplates[index - 1];
    
    await supabase.from("task_template").update({ display_order: prevTask.display_order }).eq("id", task.id);
    await supabase.from("task_template").update({ display_order: task.display_order }).eq("id", prevTask.id);
    await loadTaskTemplates(selectedStatusForTasks.id);
  };

  const moveTaskDown = async (task, index) => {
    if (index === taskTemplates.length - 1) return;
    const nextTask = taskTemplates[index + 1];
    
    await supabase.from("task_template").update({ display_order: nextTask.display_order }).eq("id", task.id);
    await supabase.from("task_template").update({ display_order: task.display_order }).eq("id", nextTask.id);
    await loadTaskTemplates(selectedStatusForTasks.id);
  };

// ==================== FUNCIONES PARA JERARQUIAS ====================
  const fetchOfficeHierarchies = async () => {
    try {
      const { data, error } = await supabase
        .from('office_hierarchy')
        .select(`
          hierarchy (
            id,
            name,
            practice_area (
              id,
              name
            ),
            hierarchy_form (
              id,
              name,
              display_order,
              case_type (
                id,
                name
              ),
              office_status (
                id,
                name,
                display_order,
                type_id
              )
            )
          )
        `)
        .eq('office_id', officeId);

      if (error) throw error

      const mapped = (data || []).map(item => item.hierarchy)
      setOfficeHierarchies(mapped)

    } catch (err) {
      console.error('Error cargando jerarquías del office:', err)
    }
  }

  const fetchHierarchies = async () => {
    try {
      const { data, error } = await supabase
        .from('hierarchy')
        .select(`
          id,
          name,
          is_template,
          practice_area ( id, name )
        `)

      if (error) throw error

      setHierarchies(data || [])
    } catch (err) {
      console.error('Error cargando jerarquías:', err)
    }
  }

  const assignHierarchyToOffice = async (hierarchy) => {
    try {
      setLoading(true)
      console.log('Asignando jerarquía al office:', hierarchy)
      let hierarchyIdToAssign = hierarchy.id

      if (hierarchy.is_template) {
        const { data: template, error: tError } = await supabase
          .from('hierarchy')
          .select(`
            id,
            name,
            practice_area_id,
            hierarchy_form (
              id,
              name,
              display_order,
              case_type ( id, name )
            )
          `)
          .eq('id', hierarchy.id)
          .single()

        if (tError) throw tError

        const { data: newHierarchy, error: hError } = await supabase
          .from('hierarchy')
          .insert({
            name: `${template.name} - ${office.name}`,
            practice_area_id: template.practice_area_id,
            parent_hierarchy_id: template.id,
            is_template: false
          })
          .select()
          .single()

        if (hError) throw hError

        for (const form of template.hierarchy_form || []) {
          const { data: newForm, error: fError } = await supabase
            .from('hierarchy_form')
            .insert({
              name: form.name,
              hierarchy_id: newHierarchy.id,
              display_order: form.display_order
            })
            .select()
            .single()

          if (fError) throw fError

          if (form.case_type?.length > 0) {
            const typesToInsert = form.case_type.map(t => ({
              name: t.name,
              hierarchy_form_id: newForm.id
            }))

            const { error: tError } = await supabase
              .from('case_type')
              .insert(typesToInsert)

            if (tError) throw tError
          }
        }

        hierarchyIdToAssign = newHierarchy.id
      }

      const { data: existing } = await supabase
        .from('office_hierarchy')
        .select('id')
        .eq('office_id', officeId)
        .eq('hierarchy_id', hierarchyIdToAssign)
        .maybeSingle()

      if (!existing) {
        const { error: ohError } = await supabase
          .from('office_hierarchy')
          .insert({
            office_id: officeId,
            hierarchy_id: hierarchyIdToAssign
          })

        if (ohError) throw ohError
      }

      await fetchOfficeHierarchies()
      setSelectedHierarchyId('')

    } catch (err) {
      console.error('Error asignando jerarquía:', err)
    } finally {
      setLoading(false)
    }
  }

  const availableHierarchies = hierarchies.filter(
    h => !officeHierarchies.some(oh => oh.id === h.id)
  )

  // ==================== COMPONENTES DE INTERFAZ ====================
  const buildSidebar = () => (
    <Box
      sx={{
        width: 260,
        backgroundColor: "#f5f5f5",
        padding: 2,
        borderRight: "1px solid #ddd",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={2} color={office.color}>
        Configuración
      </Typography>
      {[
        { id: 'office-data', label: 'Datos del despacho', icon: <Business /> },
        { id: 'positions', label: 'Puestos de trabajo', icon: <Business /> },
        { id: 'hierarchy', label: 'Jerarquía de expedientes', icon: <Assignment /> }
      ].map((section) => (
        <Button
          key={section.id}
          variant={activeSection === section ? 'contained' : 'text'}
          fullWidth
          sx={{
            justifyContent: "flex-start",
            mb: 1,
            ...(activeSection === section.id ? primaryColorStyles : {color: "black"}),
          }}
          onClick={() => setActiveSection(section.id)}
        >
          {section.label}
        </Button>
      ))}
    </Box>
  );

  const buildOfficeData = () => (
    <Box sx={{ flex: 1, padding: 3 }}>
      <Paper 
        elevation={3}
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          maxWidth: 700,
          mx: 'auto'
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          gutterBottom 
          fontWeight="bold"
          sx={{ 
            mb: { xs: 2, sm: 3 }
          }}
        >
          Datos del despacho
        </Typography>

        {/* Mensajes de estado */}
        {officeUpdateError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {officeUpdateError}
          </Alert>
        )}
        
        {officeUpdateSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ¡Datos del despacho actualizados exitosamente!
          </Alert>
        )}

        <form onSubmit={handleUpdateOffice}>
          <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <TextField
              label="Nombre del despacho"
              fullWidth
              required
              value={officeForm.name}
              onChange={handleOfficeFormChange('name')}
              size={isMobile ? "small" : "medium"}
              disabled={officeUpdateLoading}
            />

            <TextField
              label="NIF o CIF"
              fullWidth
              required
              value={officeForm.document_number}
              onChange={handleOfficeFormChange('document_number')}
              size={isMobile ? "small" : "medium"}
              disabled={officeUpdateLoading}
            />

            <TextField
              label="Teléfono de contacto"
              fullWidth
              required
              type="tel"
              value={officeForm.contact_number}
              onChange={handleOfficeFormChange('contact_number')}
              size={isMobile ? "small" : "medium"}
              disabled={officeUpdateLoading}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
            />

            <TextField
              label="Correo corporativo"
              fullWidth
              required
              type="email"
              value={officeForm.email}
              onChange={handleOfficeFormChange('email')}
              size={isMobile ? "small" : "medium"}
              disabled={officeUpdateLoading}
            />

            <Box sx={{ mt: { xs: 2, sm: 3 } }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 1.5,
                  fontWeight: 500,
                  color: 'text.secondary'
                }}
              >
                Icono del despacho
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2
              }}>
                <Button 
                  variant="outlined" 
                  component="label" 
                  startIcon={<UploadFile />}
                  size={isMobile ? "small" : "medium"}
                  disabled={officeUpdateLoading}
                  sx={{ 
                    minWidth: { xs: '100%', sm: 'auto' },
                    color: '#3d475d'
                  }}
                >
                  {iconPreview ? 'Cambiar ícono' : 'Subir ícono'}
                  <input 
                    type="file" 
                    hidden 
                    onChange={handleOfficeFileChange} 
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" 
                  />
                </Button>
                {iconPreview && (
                  <Avatar
                    src={iconPreview}
                    alt="icono preview"
                    sx={{ 
                      width: { xs: 56, sm: 64 }, 
                      height: { xs: 56, sm: 64 },
                      alignSelf: { xs: 'center', sm: 'flex-start' }
                    }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Formatos permitidos: JPG, PNG, GIF, WEBP. Tamaño máximo: 2MB
              </Typography>
            </Box>

            <FormControl fullWidth required>
              <InputLabel 
                id="color-select-label"
                size={isMobile ? "small" : "normal"}
              >
                Color principal
              </InputLabel>
              <Select
                labelId="color-select-label"
                value={officeForm.color === customColor ? 'custom' : officeForm.color}
                label="Color principal"
                onChange={handleOfficeFormChange('color')}
                size={isMobile ? "small" : "medium"}
                disabled={officeUpdateLoading}
              >
                {colorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        width: { xs: 14, sm: 16 },
                        height: { xs: 14, sm: 16 },
                        backgroundColor: option.value === 'custom' ? customColor : option.value,
                        borderRadius: '50%',
                        mr: { xs: 1, sm: 1.5 },
                        border: option.value === '#000000' ? '1px solid #ccc' : 'none'
                      }}
                    />
                    <Typography variant="body2" component="span">
                      {option.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {officeForm.color === customColor && (
              <Box sx={{ mt: 2 }}>
                <Typography 
                  variant="body2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 500,
                    color: 'text.secondary',
                    mb: 2
                  }}
                >
                  Elige un color personalizado:
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                  '& .chrome-picker': {
                    width: { xs: '100% !important', sm: 'auto !important' },
                    maxWidth: { xs: '280px !important', sm: 'none !important' }
                  }
                }}>
                  <ChromePicker
                    color={customColor}
                    onChange={handleOfficeCustomColorChange}
                    disableAlpha
                  />
                </Box>
              </Box>
            )}

            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              size={isMobile ? "medium" : "large"}
              disabled={officeUpdateLoading}
              sx={{ 
                mt: { xs: 3, sm: 4 },
                py: { xs: 1.5, sm: 2 },
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                ...primaryColorStyles
              }}
            >
              {officeUpdateLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Actualizando...
                </>
              ) : (
                'Actualizar datos'
              )}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );

  const buildJobPositions = () => {
    const plantillaInicial = [
        "Abogado",
        "Procurador",
        "Administrativo",
        "Controller/Asistente",
    ];

    const cargarPlantilla = async () => {
        const puestos = plantillaInicial.map((nombre) => ({
        name: nombre,
        office_id: office.id,
        }));

        const { data, error } = await supabase
        .from("job_position")
        .insert(puestos)
        .select();

        if (!error && data) {
        setJobPositions(data);
        } else {
        console.error("Error cargando plantilla inicial:", error.message);
        }
    };

    return (
        <Box sx={{ flex: 1, padding: 3 }}>
        <Grid container spacing={3}>
            <Grid item size={{xs: 12}}>
            <Typography variant="h5" fontWeight={600}>
                Puestos de Trabajo
            </Typography>
            </Grid>

            <Grid>
            <TextField
                label="Nuevo puesto"
                fullWidth
                value={newPositionName}
                onChange={(e) => setNewPositionName(e.target.value)}
            />
            </Grid>

            <Grid item>
            <Button
                variant="contained"
                fullWidth
                onClick={addJobPosition}
                sx={{...primaryColorStyles, height: '100%'}}
            >
                AGREGAR
            </Button>
            </Grid>

            <Grid item size={{xs: 12}}>
            {!loading && jobPositions.length === 0 ? (
                <Box>
                <Typography color="text.secondary" mb={1}>
                    No hay puestos registrados.
                </Typography>
                <Typography color="text.secondary" mb={1}>
                    Valores sugeridos: Abogado, Procurador, Administrativo, Controller/Asistente
                </Typography>
                <Button variant="outlined" onClick={cargarPlantilla}>
                    Cargar plantilla sugerida
                </Button>
                </Box>
            ) : (
                <Grid container spacing={2}>
                {jobPositions.map((position) => (
                    <Grid item size={{xs:12, md:3, lg:2}} key={position.id}>
                    <Paper
                        elevation={2}
                        sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: 2,
                        }}
                    >
                        <Typography>{position.name}</Typography>
                        <IconButton
                        color="error"
                        onClick={() => deleteJobPosition(position.id)}
                        >
                        <Delete />
                        </IconButton>
                    </Paper>
                    </Grid>
                ))}
                </Grid>
            )}
            </Grid>
        </Grid>
        </Box>
    );
  };

  // const buildPracticeAreas = () => {
  //   const availableAreas = practiceAreas.filter(
  //     area => !officePracticeAreas.some(
  //       officeArea => officeArea.practice_area.id === area.id
  //     )
  //   );

  //   return (
  //     <Box sx={{ flex: 1, padding: 3 }}>
  //       <Grid container spacing={3}>
  //         <Grid item size={{xs:12}}>
  //           <Typography variant="h5" fontWeight={600}>
  //             Áreas de Práctica
  //           </Typography>
  //           <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
  //             Selecciona las áreas de práctica que maneja tu despacho
  //           </Typography>
  //         </Grid>

  //         <Grid item size={{xs:8}}>
  //           <Select
  //             fullWidth
  //             value={selectedPracticeAreaId}
  //             onChange={(e) => setSelectedPracticeAreaId(e.target.value)}
  //             displayEmpty
  //           >
  //             <MenuItem value="">
  //               <em>Seleccionar área de práctica</em>
  //             </MenuItem>
  //             {availableAreas.map((area) => (
  //               <MenuItem key={area.id} value={area.id}>
  //                 {area.name}
  //               </MenuItem>
  //             ))}
  //           </Select>
  //         </Grid>

  //         <Grid item size={{xs:4}}>
  //           <Button
  //             variant="contained"
  //             fullWidth
  //             onClick={addPracticeAreaToOffice}
  //             sx={{...primaryColorStyles, height: '100%'}}
  //             disabled={!selectedPracticeAreaId}
  //           >
  //             AGREGAR
  //           </Button>
  //         </Grid>

  //         <Grid item size={{xs:12}}>
  //           {officePracticeAreas.length === 0 ? (
  //             <Box>
  //               <Typography color="text.secondary" mb={1}>
  //                 No has seleccionado áreas de práctica aún.
  //               </Typography>
  //               <Typography color="text.secondary" variant="body2">
  //                 Las áreas de práctica te permitirán organizar mejor tus expedientes por especialidad jurídica.
  //               </Typography>
  //             </Box>
  //           ) : (
  //             <Grid container spacing={2}>
  //               {officePracticeAreas.map((area) => (
  //                 <Grid item size={{xs:12, md:4, lg:3}} key={area.id}>
  //                   <Paper
  //                     elevation={2}
  //                     sx={{
  //                       display: "flex",
  //                       justifyContent: "space-between",
  //                       alignItems: "center",
  //                       padding: 2,
  //                     }}
  //                   >
  //                     <Typography>{area.practice_area.name}</Typography>
  //                     <IconButton
  //                       color="error"
  //                       onClick={() => removePracticeAreaFromOffice(area.id)}
  //                     >
  //                       <Delete />
  //                     </IconButton>
  //                   </Paper>
  //                 </Grid>
  //               ))}
  //             </Grid>
  //           )}
  //         </Grid>
  //       </Grid>
  //     </Box>
  //   );
  // };

  // const buildHierarchyOld = () => (
  //   <Box sx={{ flex: 1, padding: 3 }}>
  //     <Typography variant="h5" fontWeight={600} mb={2}>
  //       Jerarquía de Expedientes
  //     </Typography>
  //     <Typography variant="body2" color="text.secondary" mb={3}>
  //       Organiza: Área → Forma → Tipo (opcional) → Estatus
  //     </Typography>

  //     {officePracticeAreas.length === 0 ? (
  //       <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f9f9f9', border: '2px dashed #ddd' }}>
  //         <Typography color="text.secondary" variant="h6" mb={1}>
  //           Sin áreas de práctica
  //         </Typography>
  //         <Typography color="text.secondary" mb={2}>
  //           Primero agrega áreas de práctica.
  //         </Typography>
  //         <Button variant="outlined" onClick={() => setActiveSection('practice-areas')}>
  //           Ir a Áreas de Práctica
  //         </Button>
  //       </Paper>
  //     ) : (
  //       officePracticeAreas.map((area) => {
  //         const forms = getFormsByOfficePracticeArea(area.id);
  //         return (
  //           <Accordion 
  //             key={area.id}
  //             expanded={expandedArea === area.id}
  //             onChange={() => setExpandedArea(expandedArea === area.id ? null : area.id)}
  //             sx={{ mb: 2 }}
  //           >
  //             <AccordionSummary expandIcon={<ExpandMore />}>
  //               <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
  //                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
  //                   <Gavel color="primary" />
  //                   <Typography variant="h6" fontWeight={600}>
  //                     {area.practice_area.name}
  //                   </Typography>
  //                   <Chip label={`${forms.length} formas`} size="small" />
  //                 </Box>
  //                 <Button 
  //                   size="small" 
  //                   variant="outlined" 
  //                   startIcon={<Add />}
  //                   onClick={(e) => {
  //                     e.stopPropagation();
  //                     openDialog('form', null, { office_practice_area_id: area.id, name: '' });
  //                   }}
  //                   sx={{ mr: 2, borderColor: office?.color, color: office?.color }}
  //                 >
  //                   Nueva Forma
  //                 </Button>
  //               </Box>
  //             </AccordionSummary>
  //             <AccordionDetails>
  //               {forms.length === 0 ? (
  //                 <Typography color="text.secondary" variant="body2">
  //                   No hay formas creadas para esta área.
  //                 </Typography>
  //               ) : (
  //                 forms.map((form) => renderFormSection(form))
  //               )}
  //             </AccordionDetails>
  //           </Accordion>
  //         );
  //       })
  //     )}
  //   </Box>
  // );

  const buildHierarchy = () => (
    <Box sx={{ flex: 1, padding: 3 }}>
      
      <Typography variant="h5" fontWeight={600} mb={2}>
        Jerarquía de Expedientes
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Organiza: Área → Forma → Tipo (opcional) → Subtipo
      </Typography>

      {/* SELECT PLANTILLA */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={8}>
          <Select
            fullWidth
            value={selectedHierarchyId}
            onChange={(e) => setSelectedHierarchyId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Seleccionar plantilla</em>
            </MenuItem>

            {availableHierarchies.map(h => (
              <MenuItem key={h.id} value={h.id}>
                {h.name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={4}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            disabled={!selectedHierarchyId}
            onClick={() => {
              const hierarchy = hierarchies.find(h => h.id === selectedHierarchyId)
              assignHierarchyToOffice(hierarchy)
            }}
          >
            Agregar
          </Button>
        </Grid>
      </Grid>

      {/* SIN JERARQUIAS */}
      {officeHierarchies.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f9f9f9', border: '2px dashed #ddd' }}>
          <Typography color="text.secondary" variant="h6" mb={1}>
            No has agregado jerarquías aún
          </Typography>
          <Typography color="text.secondary" mb={2}>
            Primero asigna una jerarquía.
          </Typography>
        </Paper>
      ) : (
        officeHierarchies.map((h) => {
          const forms = h.hierarchy_form || [];

          return (
            <Accordion
              key={h.id}
              expanded={expandedHierarchy === h.id}
              onChange={() =>
                setExpandedHierarchy(
                  expandedHierarchy === h.id ? null : h.id
                )
              }
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Gavel color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {h.practice_area?.name}
                    </Typography>
                    <Chip label={`${forms.length} formas`} size="small" />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Button
                      component="div"
                      size="small"
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDialog('form', null, {
                          hierarchy_id: h.id
                        });
                      }}
                    >
                      Nueva Forma
                    </Button>
                    <IconButton
                      component="div"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDialog('hierarchy', h);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      component="div"
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteOfficeHierarchy(h.id);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {forms.length === 0 ? (
                  <Typography color="text.secondary" variant="body2">
                    No hay formas creadas para esta jerarquía.
                  </Typography>
                ) : (
                  forms.map(form => renderFormSection(form))
                )}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}
    </Box>
  );

  const renderFormSection = (form) => {
    const types = form.case_type || [];
    const statusesWithoutType =
      form.office_status?.filter(s => !s.type_id) || [];

    return (
      <Paper key={form.id} sx={{ p: 2, mb: 2, backgroundColor: '#fafafa' }}>
        {/* Cabecera de la forma */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Assignment color="secondary" />
            <Typography variant="subtitle1" fontWeight={600}>
              {form.name}
            </Typography>
            <Chip
              label={`${types.length} tipos`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          </Box>
          <Box>
            <IconButton onClick={() => openDialog('form', form)}>
              <Edit fontSize="small" />
            </IconButton>
            <IconButton color="error" onClick={() => deleteForm(form.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {/* Sección de tipos */}
        <Box sx={{ ml: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">
              TIPOS DE EXPEDIENTE:
            </Typography>
            <Button
              sx={{color: office?.color}} size="small" startIcon={<Add />}
              onClick={() => openDialog('type', null, { hierarchy_form_id: form.id })}
            >
              Agregar Tipo
            </Button>
          </Box>

          {types.length === 0 ? (
            <Box sx={{ ml: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Sin tipos
              </Typography>
              {/* Mostrar estatus sin tipo */}
              {renderStatusesSection(form.id, null, statusesWithoutType)}
            </Box>
          ) : (
            types.map(type => renderTypeSection(type, form))
          )}
        </Box>
      </Paper>
    );
  };

  const renderTypeSection = (type, form) => {
    const statuses = form.office_status?.filter(s => s.type_id === type.id) || [];

    return (
      <Paper key={type.id} sx={{ p: 1.5, mb: 1, backgroundColor: '#fff' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={500}>
              • {type.name}
            </Typography>
            <Chip
              label={`${statuses.length} subtipo`}
              size="small"
              variant="outlined"
            />
          </Box>
          <Box>
            <IconButton size="small" onClick={() => openDialog('type', type)}>
              <Edit fontSize="small" />
            </IconButton>

            <IconButton size="small" color="error" onClick={() => deleteType(type.id)}>
              <Delete fontSize="small" />
            </IconButton>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                openDialog('status', null, {
                  form_id: form.id,
                  type_id: type.id
                })
              }
              sx={{ ml: 1, color: office?.color }}
            >
              Subtipo
            </Button>
          </Box>
        </Box>

        {renderStatusesSection(form.id, type.id, statuses)}
      </Paper>
    );
  };

  const renderStatusesSection = (formId, typeId, statuses) => {
    // Función helper para contar tareas de un estatus
    /* const getTaskCount = (statusId) => {
      // Esta es una función síncrona para mostrar el conteo
      // Necesitarás cargar los conteos previamente
      return 0; // Por ahora retorna 0, más abajo te explico cómo cargar esto
    }; */

    return (
      <Box sx={{ ml: 4, mt: 1 }}>
        {statuses.length === 0 ? (
          <>
            <Typography variant="caption" color="text.secondary">Sin subtipos.</Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                openDialog('status', null, { form_id: formId, type_id: typeId })
              }
              sx={{ ml: 1 }}
            >
              Agregar subtipo
            </Button>
          </>
        ) : (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              {statuses.map(status => {
                const taskCount = status.task_count || 0; 
                const hasTemplates = taskCount > 0;

                return (
                  <Tooltip key={status.id} title={ hasTemplates ? `${taskCount} tarea${taskCount !== 1 ? 's' : ''} configurada${taskCount !== 1 ? 's' : ''}. Click para editar` : "Click para agregar tareas plantilla" } arrow >
                    <Chip
                      label={ 
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}> 
                          <span>{status.name}</span> 
                          {hasTemplates && ( 
                            <Box 
                              component="span" 
                              sx={{ backgroundColor: office.color, color: 'white', borderRadius: '10px', padding: '0 6px', fontSize: '0.7rem', fontWeight: 'bold', ml: 0.5 }} 
                            > {taskCount} 
                            </Box> 
                        )}
                        </Box>
                      }
                      size="small" icon={<ChecklistRtl fontSize="small" />}
                      onClick={() => openTaskModal(status)}
                      onDelete={() => deleteOfficeStatus(status.id)}
                      color={hasTemplates ? "primary" : "default"}
                      variant={hasTemplates ? "filled" : "outlined"} 
                      sx={{ 
                        cursor: 'pointer', 
                        '&:hover': { 
                          backgroundColor: hasTemplates ? office.color : 'rgba(0, 0, 0, 0.08)', 
                          opacity: hasTemplates ? 0.9 : 1 
                        } 
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button
                size="small"
                startIcon={<Add />}
                sx={{ mt: 1 }}
                onClick={() =>
                  openDialog('status', null, { form_id: formId, type_id: typeId })
                }
                >
                Agregar subtipo
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  };

  const UniversalDialog = () => {
    const [inputValue, setInputValue] = useState('');
    const [selectedAreaId, setSelectedAreaId] = useState('');

    useEffect(() => {
      if (dialogOpen) {
        if (dialogConfig.type === 'hierarchy') {
          setSelectedAreaId(dialogConfig.data?.practice_area?.id || '');
        } else {
          setInputValue(dialogConfig.data?.name || dialogConfig.context?.name || '');
        }
      }
    }, [dialogOpen, dialogConfig]);

    const handleSave = () => {
      if (dialogConfig.type === 'hierarchy') {
        if (!selectedAreaId) return;

        updateHierarchy({
          id: dialogConfig.data.id,
          practice_area_id: selectedAreaId
        });

        closeDialog();
        return;
      }

      const name = inputValue.trim();
      if (!name) return;

      if (dialogConfig.data) {
        const updatedData = { 
          ...dialogConfig.data, 
          name: inputValue 
        };
        switch (dialogConfig.type) {
          case 'form':
            updateHierarchyForm(updatedData);
            break;
          case 'type':
            updateCaseType(updatedData);
            break;
          case 'status':
            updateOfficeStatus(updatedData);
            break;
          default:
            break;
        }
      } else if (dialogConfig.context) {
        switch (dialogConfig.type) {
          case 'form':
            addHierarchyForm(dialogConfig.context.hierarchy_id, name);
            break;
          case 'type':
            addCaseType(
              dialogConfig.context.hierarchy_form_id,
              name
            );
            break;
          case 'status':
            addOfficeStatus(
              dialogConfig.context.form_id,
              dialogConfig.context.type_id,
              name
            );
            break;

          default:
            break;
        }
      }
      closeDialog();
    };

    const getDialogTitle = () => {
      const action = dialogConfig.data ? 'Editar' : 'Crear';
      const entityMap = {
        hierarchy: 'Jerarquía (Área)',
        form: 'Forma',
        type: 'Tipo',
        status: 'Subtipo'
      };
      return `${action} ${entityMap[dialogConfig.type] || ''}`;
    };

    return (
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          {dialogConfig.type === 'hierarchy' ? (
            <Select
              fullWidth
              value={selectedAreaId}
              onChange={(e) => setSelectedAreaId(e.target.value)}
              displayEmpty
              sx={{ mt: 2 }}
            >
              <MenuItem value="">
                <em>Seleccionar área</em>
              </MenuItem>

              {practiceAreas.map(area => (
                <MenuItem key={area.id} value={area.id}>
                  {area.name}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label="Nombre"
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inputValue.trim()) {
                  handleSave();
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={primaryColorStyles} 
            disabled={
              dialogConfig.type === 'hierarchy'
                ? !selectedAreaId
                : !inputValue.trim()
            }
          >
            {dialogConfig.data ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", position: "relative" }}>
      <Backdrop open={loading} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {buildSidebar()}
      
      {activeSection === 'office-data' && buildOfficeData()}
      {activeSection === 'positions' && buildJobPositions()}
      {/* {activeSection === 'practice-areas' && buildPracticeAreas()} */}
      {activeSection === 'hierarchy' && buildHierarchy()} 
      
      <UniversalDialog /> 
      <TaskTemplateModal
        open={taskModalOpen}
        onClose={closeTaskModal}
        selectedStatus={selectedStatusForTasks}
        taskTemplates={taskTemplates}
        newTaskName={newTaskName}
        setNewTaskName={setNewTaskName}
        editingTask={editingTask}
        setEditingTask={setEditingTask}
        addTaskTemplate={addTaskTemplate}
        updateTaskTemplate={updateTaskTemplate}
        deleteTaskTemplate={deleteTaskTemplate}
        moveTaskUp={moveTaskUp}
        moveTaskDown={moveTaskDown}
        officeColor={office.color}
      />
    </Box>
  );
};

export default OfficeSettingsView;